import {
  logPageviewAfterLoad,
  logPageviewBeforeLoad,
} from '@bucketplace/bucket-log';
import type { NavigationResponse, User } from '@bucketplace/common';
import type { PCTopBannerContent } from '@bucketplace/design-system/bds';
import { useQuery } from '@tanstack/react-query';
import { type NextRouter, useRouter } from 'next/router';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { clientHttp } from '@/common/http';
import { useTRPC } from '@/trpc/utils';
import type {
  BannerType,
  EventUserProfileType,
  NavigationDataType,
} from '@/types';
import { airbridgeLogout } from '@/utils';

interface UserInfoType {
  user: EventUserProfileType | undefined;
  cartCount?: number;
  notificationCount?: number;
}

interface UserContextType {
  userInfo?: UserInfoType;
  rawLoginUser?: User;
  appBanner?: BannerType | null;
  pcBanner?: PCTopBannerContent[] | null;
  mobileBanner?: string | null;
  pending?: boolean;
  nav?: NavigationResponse;
  signOut?: () => void;
}

const UserContext = createContext<UserContextType>({});

const convertNavigationData = (navigationData: any): NavigationDataType => {
  const { loginUser } = navigationData;
  return {
    ...navigationData,
    rawLoginUser: loginUser,
    loginUser: loginUser
      ? {
          userId: loginUser.id,
          isAdmin: loginUser.isAdmin,
          imageUrl: loginUser.profileImageUrl,
          nickname: loginUser.nickname,
          userableType: loginUser.userableType,
        }
      : null,
  };
};

export const UserContextProvider = ({
  children,
}: {
  children: React.ReactElement;
}): React.ReactElement => {
  const router = useRouter();
  const prevLocationRef = useRef<NextRouter['asPath'] | null>(null);
  const trpc = useTRPC();
  const { data, isLoading, refetch } = useQuery(trpc.navigation.queryOptions());
  const converted = useMemo(
    () => (data ? convertNavigationData(data) : null),
    [data],
  );
  const rawLoginUser = converted?.rawLoginUser ?? null;
  const appBanner = converted?.appDownloadBanner ?? null;
  const pcBanner = converted?.storePcTopBanner?.contents ?? null;
  const mobileBanner = converted?.appDownloadBanner?.mobileNavigation ?? null;
  const nav = converted?.nav ?? null;
  const userInfo = useMemo(
    () =>
      converted
        ? {
            user: converted.loginUser,
            cartCount: converted.cartCount,
            notificationCount: converted.unseenNotification,
          }
        : undefined,
    [converted],
  );

  useEffect(() => {
    const userId = converted?.loginUser?.userId;
    (window as any).bucketLog?.push({ user_id: userId || null });
  }, [converted?.loginUser?.userId]);

  useEffect(() => {
    if (userInfo != null) {
      router.events.on('routeChangeComplete', logPageviewAfterLoad);
    }
    return () => router.events.off('routeChangeComplete', logPageviewAfterLoad);
  }, [userInfo, router.events]);
  useEffect(() => {
    if (userInfo == null) {
      return;
    }
    if (prevLocationRef.current == null) {
      prevLocationRef.current = router.asPath;
      return;
    }
    const prevPath = prevLocationRef.current;
    const currPath = router.asPath;
    if (prevPath !== currPath) {
      prevLocationRef.current = currPath;
      logPageviewBeforeLoad(prevPath, currPath);
    }
  }, [prevLocationRef, router, userInfo]);
  const signOut = useCallback(async () => {
    const json = await clientHttp.delete('/api/navigation/signOut').json<any>();
    if (!json.success) {
      return;
    }
    await refetch();
    (window as any).bucketLog.push({ user_id: null });
    airbridgeLogout();
  }, [refetch]);

  const userValue = useMemo<UserContextType>(
    () => ({
      userInfo,
      appBanner,
      pcBanner,
      mobileBanner,
      pending: isLoading,
      signOut,
      nav: nav ?? undefined,
      rawLoginUser: rawLoginUser ?? undefined,
    }),
    [
      appBanner,
      pcBanner,
      mobileBanner,
      isLoading,
      signOut,
      userInfo,
      nav,
      rawLoginUser,
    ],
  );
  return (
    <UserContext.Provider value={userValue}>{children}</UserContext.Provider>
  );
};

export const useUserContext = (): UserContextType => {
  const context = useContext(UserContext);
  return context;
};
